import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { LANDING_PAGE_WIDTH, MIN_DESKTOP_THRESHOLD } from '~/core/constants'
import { TitleH4, TitleH5 } from '../typography'

import { ColumnFlex } from '~/components/common/containers'

const features = [
  {
    title: 'Build your excellent video experience',
    items: [
      { title: 'HD Audio', description: 'Opus Codec' },
      { title: 'Full HD Video', description: 'Ability to support 1080p video' },
      { title: 'Group chat and private chat', description: 'Easily send data and chat messages between all participants in a video session' },
      { title: 'Desktop screen sharing', description: 'Share your computer screen and choose which applications or monitors to display' },
      { title: 'Mobile screen sharing', description: 'Share your mobile screen directly from your mobile phone' },
      { title: 'Maximum participants', description: 'Currently we support up to 100 participants who can join the same meeting' },
      { title: 'Maximum concurrent meetings', description: 'Unlimited' },
      { title: 'Bandwidth controls', description: 'Users can monitor connectivity quality and adjust video bandwidth' },
      { title: 'Raise your hand', description: 'Participants can discreetly indicate they have something to say without interrupting the current speaker' },
      { title: 'Cross platform and devices', description: 'Easy iFrame integration for web, Mac and Windows, as well as mobile SDKs for iOS phone and tablets, and Android phones and tablets' },
      { title: 'Customizable UI', description: 'Configure, show/hide UI elements and controls, to build the experience you want end users to have' },
      { title: 'Configure, enable and disable features', description: 'Configure, enable, and disable features to build the experience you want end users to have' },
      { title: 'Branding – logo, background, invite link, dial-in numbers link', description: 'Brand the experience with your company information and show only what you need' },
      { title: 'Ability to record a meeting', description: 'Record the audio, video, and screen share from a meeting. Save it to reference later or to send to those who could not attend' },
      { title: 'YouTube Video Sharing', description: 'Users can share a YouTube video for everyone in the conference to see' },
      { title: 'Phone access to meetings', description: 'Call into meetings using 80+ dial in number options (11 toll-free) for 58 countries' },
      { title: 'Remote desktop control through electron integrations', description: 'Control the mouse and keyboard movements of another user remotely through electron integrations' }
    ]
  },
  {
    title: 'High security, reliability and compliances',
    items: [
      { title: 'Authorized and secure access to your meetings', description: 'Secure your meetings. Your Attendees. Your Controls' },
      { title: 'Secure passcodes', description: 'The ability to set a passcodes to provide an additional layer of security' },
      { title: 'Industry-leading Service Levels', description: '99.99% Platform Uptime SLAs' },
      { title: 'GDPR compliant', description: 'GDPR compliant for data processors' },
      { title: 'HIPAA Compatible', description: '8x8 is HIPAA compatible and can provide a BAA. It is incumbent upon users to operate the technology in a HIPAA compliant manner' },
      { title: 'Regional server distribution and global low latency', description: '8x8 Jaas has media servers in nine regions distributed worldwide. Use Global Low Latency to automatically optimize for the best connection between participants' },
      { title: 'End-to-end encryption', description: 'True end to end encryption even with videobridge for desktop video meetings' }
    ]
  },
  {
    title: 'Reach thousands with live streaming',
    items: [
      { title: 'RTMP streaming', description: 'Stream sessions live to YouTube, or any other RTMP services' }
    ]
  },
  {
    title: 'Easily manage your services',
    items: [
      { title: 'Comprehensive account dashboard', description: 'See your activity and usage, manage your plan and team, manage your billing, and setup your branding ' },
      { title: 'Developer tools', description: 'Developer tools which allow you to manage your API keys, access tokens, sample app and code snippets, to help you setup, build and run your video conferencing integration' },
      { title: 'Webhooks', description: 'Provide your application with relevant meeting event responses' },
      { title: 'Webhook metrics and persistence', description: 'Easily monitor and analyze your event integrations' },
      { title: 'Advanced meeting analytics', description: 'Get access to in-depth analysis about quality of service and usage metrics' },
      { title: 'Customer support', description: 'Customer support included in the service' }
    ]
  }
]

const FeaturesContainer = styled.div`
  color: #131519;
  font-size: 14px;
  line-height: 16px;
  margin-top: 16px;
  width: 100%;
`

const Feature = styled(ColumnFlex)`
  padding: 16px;
  border-bottom: 1px solid #F2F3F4;
`

const Title = styled.span`
  font-weight: 500;
`

const Description = styled.span`
  margin-top: 8px;
`

const Container = styled(ColumnFlex)`
  margin: 40px 24px 16px 24px;

  ${TitleH4} {
    margin: 0 auto 8px;
    text-align: center;
  }

  ${TitleH5} {
    margin: 24px auto;
    text-align: center;
    display: block;
  }

  @media screen and (min-width: ${MIN_DESKTOP_THRESHOLD}) {
    width: ${LANDING_PAGE_WIDTH};

    ${Feature} {
      padding: 0;
      display: grid;
      grid-template-columns: 320px 1fr;

      ${Title}, ${Description} {
        margin: 16px;
      }
    }
  }
`

const Features = () => {
  const node = useRef()

  useEffect(() => {
    if (window.location.hash === '#features') {
      node.current.scrollIntoView()
    }
  }, [])

  return (
    <Container ref={node}>
      <TitleH4>Jitsi Video Platform – Comprehensive meetings features for every plan</TitleH4>
      <FeaturesContainer>
        {features.map(({ title, items }) => (
          <React.Fragment key={title}>
            <TitleH5>{title}</TitleH5>
            {items.map(({ title, description }) => (
              <Feature key={title}>
                <Title>{title}</Title>
                <Description>{description}</Description>
              </Feature>
            ))}
          </React.Fragment>
        ))}
      </FeaturesContainer>
    </Container>
  )
}

export default Features
